import {
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    Typography,
    Box,
    Tooltip,
} from "@mui/material";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "../../../../explorer/hooks/useTranslation";
import SkeletonList from "../../../pages/SpareParts/HomePage/SkeletonList/SkeletonList";
import { TColumns, ColumnWidth, ColumnAlign } from "../PartList";
import { useTableLayoutClasses } from "./TableLayout.style";
import ScrollTop from "../../ScrollTop/ScrollTop";

type TTablelayout = {
    items: any;
    loading?: boolean;
    handleScroll?: (event: React.UIEvent<HTMLElement>) => void;
    columns: TColumns[];
};

interface ITablelayout extends TTablelayout {
    calloutNotified?: string;
    notifyCallout?: (position?: string) => void;
    notifyPart?: (part?: string) => void;
    partNotified?: string;
    showPart?: string;
    emptyPage?: React.ElementType;
    onRowClick?: (
        code: string,
        replacementCode: string,
        manufacturer: string,
        position: string,
        parentNaturalKey: string,
        item?: any
    ) => void;
    onOfferOrderRowClick?: (
        offerCode: string,
        orderCode: string,
        orderType: string,
        customerOfferCode: string,
        customerOrderCode: string
    ) => void;
}

const TableLayout = (props: ITablelayout) => {
    const [selectedRowPart] = useState<string>();
    const [showPartState, setShowPartState] = useState(props.showPart);

    var timeout: NodeJS.Timeout;
    var timeout2: NodeJS.Timeout;

    useEffect(() => {
        if (
            !props.loading &&
            props.showPart !== undefined &&
            props.notifyCallout !== undefined
        ) {
            timeout = setTimeout(function () {
                var elems = document.querySelectorAll(
                    "[data-plpart='" +
                        props.showPart!.replaceAll("'", "") +
                        "']"
                );
                if (elems !== undefined && elems !== null && elems.length > 0) {
                    let celem: Element | null = elems[0];
                    let position = celem.getAttribute("data-plposition");

                    if (props.notifyPart !== undefined) {
                        props.notifyPart(position!);
                    }
                    if (props.notifyCallout !== undefined) {
                        props.notifyCallout(position!);
                    }
                }
            }, 1000);
        }
    }, [props.loading]);

    useEffect(() => {
        setShowPartState(props.showPart);
    }, [props.showPart]);

    const { t } = useTranslation();
    const classes = useTableLayoutClasses();

    useEffect(() => {
        if (
            props.calloutNotified !== undefined &&
            props.calloutNotified !== ""
        ) {
            var elems = document.querySelectorAll(
                "[data-plposition='" + props.calloutNotified + "']"
            );
            if (elems !== undefined && elems !== null && elems.length > 0) {
                var celem: Element | null = elems[0];
                var prevElem: Element | null = null;
                if (
                    celem.previousElementSibling !== null &&
                    celem.previousElementSibling !== undefined
                )
                    prevElem = celem.previousElementSibling;

                if (prevElem === null) {
                    celem.scrollIntoView({
                        behavior: "smooth",
                        block: "end",
                        inline: "start",
                    });
                } else {
                    prevElem.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                        inline: "start",
                    });
                }
            }
        }
    }, [props.calloutNotified]);

    useEffect(() => {
        if (props.partNotified !== undefined && props.partNotified !== "") {
            var elems = document.querySelectorAll(
                "[data-plpart='" + props.partNotified.replaceAll("'", "") + "']"
            );
            if (elems !== undefined && elems !== null && elems.length > 0) {
                var celem: Element | null = elems[0];
                var prevElem: Element | null = null;
                if (
                    celem.previousElementSibling !== null &&
                    celem.previousElementSibling !== undefined
                )
                    prevElem = celem.previousElementSibling;

                if (prevElem === null) {
                    celem.scrollIntoView({
                        behavior: "smooth",
                        block: "end",
                        inline: "start",
                    });
                } else {
                    prevElem.scrollIntoView({
                        behavior: "smooth",
                        block: "start",
                        inline: "start",
                    });
                }
            }
        }
    }, [props.partNotified]);

    const rowMouseOver = (
        event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
        position?: string,
        part?: string
    ) => {
        if (props.notifyCallout !== undefined) {
            clearTimeout(timeout);
            timeout = setTimeout(function () {
                if (props.notifyCallout !== undefined)
                    props.notifyCallout(position);
            }, 500);
        }
        /*
        if (props.notifyPart !== undefined) {
            clearTimeout(timeout2);
            timeout2 = setTimeout(function () {
                if (props.notifyPart !== undefined) props.notifyPart(part);
            }, 500);
        }
            */
    };

    const rowMouseOut = (
        event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
        position?: string
    ) => {
        if (props.notifyCallout !== undefined) {
            clearTimeout(timeout);
            props.notifyCallout("");
        }
        if (props.notifyPart !== undefined) {
            clearTimeout(timeout2);
            props.notifyPart("");
        }
    };

    const rowMouseClick = (
        event: React.MouseEvent<HTMLTableRowElement, MouseEvent>,
        position?: string
    ) => {};
    const classByWidth = (colWidth?: ColumnWidth) => {
        switch (colWidth) {
            case ColumnWidth.IconWidth:
                return classes.iconColumn;
            case ColumnWidth.TextWidth:
                return classes.textColumn;
            case ColumnWidth.ShortTextWidth:
                return classes.shortTextColumn;
            default:
                return classes.textColumn;
        }
    };

    const getHeaderCell = (col: TColumns) => {
      const hText: string = col.headerText ? col.headerText : t(`common.spareparts.part.${col.name}`);
      return col.viewHeader ? (
        <Box sx={classes.spanInside} component="span">
          {col.useHeaderTooltip ? (
            <Tooltip title={hText} placement="top">
              <span> {hText}</span>
            </Tooltip>
          ) : (
            <> {hText}</>
          )}
        </Box>
      ) : (
        ""
      );
    };

    const scrollRef = useRef<HTMLDivElement | null>(null);

    return (
        <TableContainer
            component="div"
            sx={classes.tableContainer}
            ref={scrollRef}
            onScroll={props.handleScroll}
        >
            {props.loading ? (
                <SkeletonList
                    mkey={"TableLayout"}
                    hasIconBefore
                    useIcons
                    hasHeader
                    cols={3}
                    iconCols={2}
                    rowsForCell={1}
                    rows={10}
                />
            ) : (
                <Table
                    stickyHeader
                    size="small"
                    sx={classes.table}
                    aria-label="simple table"
                >
                    <TableHead>
                        <TableRow>
                            {props.columns.map((col) => (
                                <TableCell
                                    key={col.name}
                                    className={col.name}
                                    sx={[
                                        classByWidth(col.width),
                                        classes.headerColumn,
                                    ]}
                                    style={{
                                        textAlign:
                                            col.align ?? ColumnAlign.Center,
                                    }}
                                    children={
                                        getHeaderCell(col)
                                    }
                                />
                            ))}
                        </TableRow>
                    </TableHead>
                    {props.items !== undefined &&
                        props.items !== null &&
                        props.items.length > 0 && (
                            <TableBody>
                                {props.items.map(
                                    (item: any, mapIndex: number) => (
                                        <TableRow
                                            key={"table_row_" + mapIndex}
                                            hover
                                            onClick={(event) => {
                                                if (
                                                    props.onRowClick !==
                                                        undefined) {
                                                    props.onRowClick(
                                                        item.code,
                                                        item.replacementCode,
                                                        item.manufacturerCompanyCode,
                                                        item.position,
                                                        item.parentNaturalKey,
                                                        item
                                                    );
                                                } else if (
                                                    props.onOfferOrderRowClick !==
                                                    undefined
                                                ) {
                                                    props.onOfferOrderRowClick(
                                                        item.offerCode,
                                                        item.orderCode,
                                                        item.orderType,
                                                        item.customerOfferCode,
                                                        item.customerOrderCode
                                                    );
                                                }
                                            }}
                                            selected={
                                                (item.code ===
                                                    selectedRowPart &&
                                                    item.code !== undefined) ||
                                                (props.calloutNotified !==
                                                    undefined &&
                                                    props.calloutNotified ===
                                                        item.position)
                                            }
                                            onMouseOverCapture={(event) =>
                                                rowMouseOver(
                                                    event,
                                                    item.position,
                                                    item.code
                                                )
                                            }
                                            onMouseOutCapture={(event) =>
                                                rowMouseOut(
                                                    event,
                                                    item.position
                                                )
                                            }
                                            className={
                                                (props.calloutNotified !==
                                                    undefined &&
                                                    props.calloutNotified ===
                                                        item.position) ||
                                                (props.partNotified !==
                                                    undefined &&
                                                    props.partNotified ===
                                                        item.code) ||
                                                (showPartState !== undefined &&
                                                    showPartState === item.code)
                                                    ? "selected"
                                                    : ""
                                            }
                                            style={{
                                                cursor:
                                                    item.showBasket == 1 ||
                                                    props.onRowClick ||
                                                    props.onOfferOrderRowClick
                                                        ? "pointer"
                                                        : undefined,
                                            }}
                                            role="checkbox"
                                            tabIndex={-1}
                                            data-plposition={item.position}
                                            data-plpart={item.code}
                                        >
                                            {props.columns.map(
                                                (
                                                    col: TColumns,
                                                    index: number
                                                ) => (
                                                    <TableCell
                                                        key={`${index}_${col.name}`}
                                                        className={col.name}
                                                        sx={[
                                                            classes.textColumn,
                                                            classes.bodyColumn,
                                                        ]}
                                                        style={{
                                                            textAlign:
                                                                col.align ??
                                                                ColumnAlign.Center,
                                                        }}
                                                    >
                                                        {col.customComponent !==
                                                        undefined ? (
                                                            <col.customComponent
                                                                item={item}
                                                                additionalProps={
                                                                    col.additionalProps
                                                                }
                                                                colName={
                                                                    col.name
                                                                }
                                                            />
                                                        ) : (
                                                            <Typography
                                                                variant="body2"
                                                                noWrap={true}
                                                                component="div"
                                                            >
                                                                {
                                                                    (
                                                                        item as {
                                                                            [
                                                                                key: string
                                                                            ]: any;
                                                                        }
                                                                    )[col.name]
                                                                }
                                                            </Typography>
                                                        )}
                                                    </TableCell>
                                                )
                                            )}
                                        </TableRow>
                                    )
                                )}
                            </TableBody>
                        )}
                </Table>
            )}
            {(props.items === undefined ||
                props.items === null ||
                props.items.length === 0) &&
                !props.loading &&
                props.emptyPage && <props.emptyPage />}
            <ScrollTop scrollItemRef={scrollRef} />
        </TableContainer>
    );
};

export default TableLayout;
